"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyRedemptionRounding = exports.LOCKED_REDEMPTION_SETTINGS_FIELDS = exports.isOpenRedemptionStatus = exports.OPEN_REDEMPTION_STATUSES = exports.isRedemptionIntakeOpen = exports.isRedemptionSettingsComplete = exports.getMissingRedemptionSettingsFields = exports.areValidCustomDealingDates = exports.isValidDealingCutoff = exports.toDealingDate = exports.dealingDayKey = exports.isSupportedTimezone = exports.REDEMPTION_CUTOFF_TIME_PATTERN = exports.isNonNegativeInteger = exports.REDEMPTION_SETTINGS_FIELD_LABELS = exports.MAX_CUSTOM_DEALING_DATES = void 0;
const enums_1 = require("./enums");
/** Maximum explicit dealing dates a fund may list when `dealingFrequency` is `custom`. */
exports.MAX_CUSTOM_DEALING_DATES = 8;
exports.REDEMPTION_SETTINGS_FIELD_LABELS = {
    allowRedemptions: "Allow investors to redeem",
    dealingFrequency: "Dealing frequency",
    customDealingDates: "Custom dealing dates",
    cutoff: "End of day",
    minimumRedemptionUnits: "Minimum redemption units",
    minimumRemainingHoldingUnits: "Minimum remaining holding units",
    roundingMethod: "Rounding method",
    payoutMethod: "Payout method",
};
const dealingFrequencyValues = new Set(Object.values(enums_1.DealingFrequency));
const roundingMethodValues = new Set(Object.values(enums_1.RoundingMethod));
const payoutMethodValues = new Set(Object.values(enums_1.RedemptionPayoutMethod));
const isNonNegativeInteger = (value) => typeof value === "number" && Number.isInteger(value) && value >= 0;
exports.isNonNegativeInteger = isNonNegativeInteger;
const isInSet = (values, value) => Boolean(value?.trim() && values.has(value.trim()));
// Trimmed everywhere: a padded `" custom "` would pass the membership check but fail strict equality,
// skipping the custom-date rules entirely.
const readDealingFrequency = (schedule) => schedule?.dealingFrequency?.trim();
/** 24-hour `HH:mm`. Shared with the write-time schema so both gates accept the same times. */
exports.REDEMPTION_CUTOFF_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
// A zone the runtime can format in. `AEST`, `GMT+10` and typos pass a non-empty check but make every
// later `Intl`/`date-fns-tz` call throw `RangeError`.
const isSupportedTimezone = (timezone) => {
    if (!timezone) {
        return false;
    }
    try {
        // Untrimmed, unlike the enums above: consumers hand the stored string straight to `Intl`, so
        // tolerated padding would still throw at the point of use.
        Intl.DateTimeFormat(undefined, { timeZone: timezone });
        return true;
    }
    catch {
        return false;
    }
};
exports.isSupportedTimezone = isSupportedTimezone;
// Fund-local calendar day, the granularity the window key uses: two dates sharing one fund-local day
// derive the same window even when their UTC days differ. Shared with the write-time schema.
const dealingDayKey = (date, timezone) => date.toLocaleDateString("en-CA", timezone ? { timeZone: timezone } : undefined);
exports.dealingDayKey = dealingDayKey;
// Values arrive as ISO strings and can be anything at runtime; `new Date()` maps `null`, `0` and
// `false` onto the epoch, which would read as a legitimate 1970 dealing date.
const toDealingDate = (value) => {
    if (!(value instanceof Date) && typeof value !== "string") {
        return null;
    }
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
};
exports.toDealingDate = toDealingDate;
/** Weekday is restricted to 1-5: a fund deals on business days only. */
const isValidDealingCutoff = (cutoff) => Boolean(cutoff &&
    Number.isInteger(cutoff.weekday) &&
    cutoff.weekday >= 1 &&
    cutoff.weekday <= 5 &&
    exports.REDEMPTION_CUTOFF_TIME_PATTERN.test(cutoff.time ?? "") &&
    (0, exports.isSupportedTimezone)(cutoff.timezone));
exports.isValidDealingCutoff = isValidDealingCutoff;
const areValidCustomDealingDates = (schedule) => {
    if (readDealingFrequency(schedule) !== enums_1.DealingFrequency.CUSTOM) {
        return true;
    }
    const dates = schedule?.customDealingDates ?? [];
    if (dates.length === 0 || dates.length > exports.MAX_CUSTOM_DEALING_DATES) {
        return false;
    }
    const parsed = dates.map(exports.toDealingDate);
    if (parsed.some((date) => date === null)) {
        return false;
    }
    // Falls back to the runtime zone when the end-of-day timezone is unusable — that case already
    // reports the end of day as missing.
    const timezone = (0, exports.isSupportedTimezone)(schedule?.cutoff?.timezone) ? schedule?.cutoff?.timezone : undefined;
    const days = parsed.map((date) => (0, exports.dealingDayKey)(date, timezone));
    return new Set(days).size === days.length;
};
exports.areValidCustomDealingDates = areValidCustomDealingDates;
// `allowRedemptions` is excluded: settings can be complete while redemptions are switched off.
const getMissingRedemptionSettingsFields = (config) => {
    const missing = [];
    if (!isInSet(dealingFrequencyValues, config?.dealingFrequency)) {
        missing.push(exports.REDEMPTION_SETTINGS_FIELD_LABELS.dealingFrequency);
    }
    if (!(0, exports.areValidCustomDealingDates)(config)) {
        missing.push(exports.REDEMPTION_SETTINGS_FIELD_LABELS.customDealingDates);
    }
    if (!(0, exports.isValidDealingCutoff)(config?.cutoff)) {
        missing.push(exports.REDEMPTION_SETTINGS_FIELD_LABELS.cutoff);
    }
    if (!(0, exports.isNonNegativeInteger)(config?.minimumRedemptionUnits)) {
        missing.push(exports.REDEMPTION_SETTINGS_FIELD_LABELS.minimumRedemptionUnits);
    }
    if (!(0, exports.isNonNegativeInteger)(config?.minimumRemainingHoldingUnits)) {
        missing.push(exports.REDEMPTION_SETTINGS_FIELD_LABELS.minimumRemainingHoldingUnits);
    }
    if (!isInSet(roundingMethodValues, config?.roundingMethod)) {
        missing.push(exports.REDEMPTION_SETTINGS_FIELD_LABELS.roundingMethod);
    }
    if (!isInSet(payoutMethodValues, config?.payoutMethod)) {
        missing.push(exports.REDEMPTION_SETTINGS_FIELD_LABELS.payoutMethod);
    }
    return missing;
};
exports.getMissingRedemptionSettingsFields = getMissingRedemptionSettingsFields;
const isRedemptionSettingsComplete = (config) => (0, exports.getMissingRedemptionSettingsFields)(config).length === 0;
exports.isRedemptionSettingsComplete = isRedemptionSettingsComplete;
const isRedemptionIntakeOpen = (config) => config?.allowRedemptions === true && (0, exports.isRedemptionSettingsComplete)(config);
exports.isRedemptionIntakeOpen = isRedemptionIntakeOpen;
/** Units claimed but still on the register. Shared so the form's ceiling and intake cannot disagree. */
exports.OPEN_REDEMPTION_STATUSES = ["requestReceived", "approvedInPrinciple"];
const isOpenRedemptionStatus = (status) => exports.OPEN_REDEMPTION_STATUSES.includes(status);
exports.isOpenRedemptionStatus = isOpenRedemptionStatus;
/**
 * Frozen while the fund holds an open request: an undecided request derives its dealing date from the
 * schedule and was validated against the minimums. `allowRedemptions` is absent deliberately — it is how
 * the admin stops new requests so the queue can drain and the lock can release.
 */
exports.LOCKED_REDEMPTION_SETTINGS_FIELDS = [
    "dealingFrequency",
    "customDealingDates",
    "cutoff",
    "minimumRedemptionUnits",
    "minimumRemainingHoldingUnits",
    "roundingMethod",
    "payoutMethod",
];
// Takes and returns cents. Truncate floors to the whole dollar, not the nearest cent.
const applyRedemptionRounding = (amountCents, method) => {
    // Binary-fraction noise cancelled first (8.2 * 100 = 819.999…), so an exact whole-cent product is
    // not flipped a cent by the method.
    const cents = Math.round(amountCents * 1e6) / 1e6;
    if (method === enums_1.RoundingMethod.ROUND_UP) {
        return Math.ceil(cents);
    }
    if (method === enums_1.RoundingMethod.TRUNCATE) {
        return Math.floor(cents / 100) * 100;
    }
    return Math.floor(cents);
};
exports.applyRedemptionRounding = applyRedemptionRounding;
//# sourceMappingURL=IRedemptionConfig.js.map